import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  Button,
  Text,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  HStack,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import transformValidationErrors from 'helpers/transformValidationErrors';
import { useAppSelector, useAppDispatch } from 'hooks';

import {
  apiSaveSpecialtyForm,
  apiGetSpecialtyFiles,
  apiSubmitSpecialtyForm,
} from 'api';
import {
  setHasUnsavedChanges,
  setValidationErrors,
  clearValidationErrors,
  setWipStatus,
} from 'reducers/specialtyFormReducer';
import { SPECIALTY_FILE_TYPES } from 'constants/fileTypes';
import type { APIApplicationFile } from 'types/api.types';
import { WIP_STATUS_BINDER_PENDING_UPLOAD } from 'variables/constants';
import { useSpecialtyCarriers } from 'hooks/index';

interface MissingFileRequirement {
  type:
    | 'declaration'
    | 'prior_policy_declaration'
    | 'mvr'
    | 'dash'
    | 'loa_signed_document';
  label: string;
}

interface SpecialtySubmitProps {
  isLocked?: boolean;
  canEditPremium?: boolean;
}

const SpecialtySubmit = ({
  isLocked = false,
  canEditPremium = false,
}: SpecialtySubmitProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingJiraTicket, setIsCreatingJiraTicket] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');

  const formData = useAppSelector((state) => state.specialtyForm.data);
  const hasUnsavedChanges = useAppSelector(
    (state) => state.specialtyForm.hasUnsavedChanges,
  );
  const isLOA = useAppSelector(
    (state) => state.specialtyForm.data.policy_information.is_loa,
  );
  const lineOfBusiness = useAppSelector(
    (state) => state.specialtyForm.lineOfBusiness,
  );
  const premium = useAppSelector((state) => state.specialtyForm.data.premium);
  const premiumConfirmed = useAppSelector(
    (state) => state.specialtyForm.premiumConfirmed,
  );

  const { availableCarriers } = useSpecialtyCarriers(
    lineOfBusiness || undefined,
  );

  // Query for existing files
  const { data: files = [] } = useQuery({
    queryKey: ['specialty-files', uuid],
    queryFn: () => apiGetSpecialtyFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  // Check if all required fields are filled
  const areRequiredFieldsFilled = useMemo(() => {
    const baseFields =
      formData.insurance_company &&
      formData.effective_date &&
      formData.binder_policy_number &&
      formData.premium &&
      formData.client_name &&
      formData.disclosure_and_attestation?.broker_name;

    const isAgencyBill = formData.billing.payment_method === 'agency_bill';

    // For LOA, check LOA-specific fields, skip business_type
    if (isLOA) {
      const loaFields =
        formData.policy_information.loa_prior_broker &&
        formData.policy_information.loa_prior_policy_number;

      if (isAgencyBill) {
        return !!(
          baseFields &&
          loaFields &&
          formData.billing.province &&
          formData.billing.business_code
        );
      }

      return !!(baseFields && loaFields && formData.billing.payment_schedule);
    }

    // For regular submissions, business_type is required
    if (!formData.business_type) return false;

    if (isAgencyBill) {
      return !!(
        baseFields &&
        formData.billing.province &&
        formData.billing.business_code
      );
    }

    return !!(baseFields && formData.billing.payment_schedule);
  }, [formData, isLOA]);

  // Get missing file requirements (single source of truth)
  const missingFileRequirements = useMemo<MissingFileRequirement[]>(() => {
    const missing: MissingFileRequirement[] = [];

    if (isLOA) {
      // LOA requires prior declaration pages, current declaration pages, and signed LOA
      const hasPriorDeclaration = files.some(
        (file) =>
          file.file_type ===
          SPECIALTY_FILE_TYPES.PRIOR_POLICY_DECLARATION_PAGES,
      );
      if (!hasPriorDeclaration) {
        missing.push({
          type: 'prior_policy_declaration',
          label: 'Prior Policy Declaration (Deck) Page',
        });
      }

      const hasCurrentDeclaration = files.some(
        (file) => file.file_type === SPECIALTY_FILE_TYPES.DECLARATION_PAGES,
      );
      if (!hasCurrentDeclaration) {
        missing.push({
          type: 'declaration',
          label: 'Current Declaration (Deck) Page',
        });
      }

      const hasSignedLOA = files.some(
        (file) => file.file_type === SPECIALTY_FILE_TYPES.LOA_SIGNED_DOCUMENT,
      );
      if (!hasSignedLOA) {
        missing.push({
          type: 'loa_signed_document',
          label: 'Signed LOA Document',
        });
      }

      // For Aviva Elite LOA, also require MVR
      if (
        formData.insurance_company === 'Aviva Elite' &&
        lineOfBusiness === 'Auto'
      ) {
        const selectedCarrier = availableCarriers.find(
          (c) => c.name === formData.insurance_company,
        );
        if (selectedCarrier?.fileRequirements.mvrReport) {
          const hasMVR = files.some(
            (file) => file.file_type === SPECIALTY_FILE_TYPES.MVR_REPORT,
          );
          if (!hasMVR) {
            missing.push({
              type: 'mvr',
              label: 'MVR Upload (per driver)',
            });
          }
        }
      }
    } else {
      // Declaration pages are required only when Direct Bill is selected
      const isDirectBill = formData.billing.payment_method !== 'agency_bill';
      if (isDirectBill) {
        const hasDeclaration = files.some(
          (file) => file.file_type === SPECIALTY_FILE_TYPES.DECLARATION_PAGES,
        );
        if (!hasDeclaration) {
          missing.push({ type: 'declaration', label: 'Declaration Pages' });
        }
      }
    }

    return missing;
  }, [
    files,
    isLOA,
    formData.insurance_company,
    formData.billing.payment_method,
    lineOfBusiness,
    availableCarriers,
  ]);

  // Check if all required files are uploaded
  const areRequiredFilesUploaded = useMemo(() => {
    return missingFileRequirements.length === 0;
  }, [missingFileRequirements]);

  // Check if premium confirmation is required and completed
  const isPremiumConfirmed = premium <= 10000 || premiumConfirmed;

  // Check if form can be submitted
  const canSubmit = useMemo(() => {
    return (
      areRequiredFieldsFilled &&
      areRequiredFilesUploaded &&
      !hasUnsavedChanges &&
      isPremiumConfirmed
    );
  }, [
    areRequiredFieldsFilled,
    areRequiredFilesUploaded,
    hasUnsavedChanges,
    isPremiumConfirmed,
  ]);

  // Get missing requirements message
  const getMissingRequirementsMessage = useCallback(() => {
    const missing: string[] = [];

    if (hasUnsavedChanges) {
      missing.push('Please save your changes before submitting');
    }

    if (!isPremiumConfirmed) {
      missing.push(
        'Please confirm the premium amount is correct (premium exceeds $10,000)',
      );
    }

    if (!areRequiredFieldsFilled) {
      missing.push('All required fields must be filled');
    }

    // Add missing file labels
    missingFileRequirements.forEach((req) => {
      missing.push(req.label);
    });

    return missing;
  }, [
    hasUnsavedChanges,
    areRequiredFieldsFilled,
    missingFileRequirements,
    isPremiumConfirmed,
  ]);

  const handleSave = async () => {
    setIsSaving(true);

    // Clear previous validation errors
    dispatch(clearValidationErrors());

    try {
      const dataToSave = {
        ...formData,
        premium_confirmed: premiumConfirmed,
      };
      const response = await apiSaveSpecialtyForm(uuid!, dataToSave);

      // Mark as saved
      dispatch(setHasUnsavedChanges(false));

      toast({
        title: 'Success',
        description: response.detail,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      // Check if this is a validation error
      const errorData = error?.response?.data || error;
      const hasValidationErrors =
        typeof errorData === 'object' &&
        errorData !== null &&
        Object.keys(errorData).some(
          (key) =>
            [
              'insurance_company',
              'effective_date',
              'binder_policy_number',
              'business_type',
              'client_name',
              'customer_code',
              'billing',
              'disclosure_and_attestation',
              'loa_prior_broker',
              'loa_prior_policy_number',
            ].includes(key) || errorData[key],
        );

      if (hasValidationErrors) {
        const transformedErrors = transformValidationErrors(errorData);
        dispatch(setValidationErrors(transformedErrors));

        toast({
          title: 'Validation Error',
          description: 'Please fix the errors in the form and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: 'Error',
          description:
            errorData?.error ||
            error?.message ||
            'Failed to save form data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForm = async () => {
    setIsCreatingJiraTicket(true);

    try {
      const response = await apiSubmitSpecialtyForm(uuid!);

      dispatch(setWipStatus(WIP_STATUS_BINDER_PENDING_UPLOAD));

      toast({
        title: 'Success',
        description: response.detail || 'Form submitted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to submit form. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreatingJiraTicket(false);
    }
  };

  const missingRequirements = getMissingRequirementsMessage();

  return (
    <>
      {/* Form Submission Card - Hide when locked */}
      {!isLocked && (
        <Card mb="20px">
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="bold">
              Form Submission
            </Text>

            {!canSubmit && missingRequirements.length > 0 && (
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  <Text fontWeight="bold" mb={1}>
                    Please complete the following before submitting:
                  </Text>
                  <ul style={{ marginLeft: '20px' }}>
                    {missingRequirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {canSubmit && (
              <Alert status="success">
                <AlertIcon />
                <AlertDescription>
                  All required fields and files are complete. You can now submit
                  the form.
                </AlertDescription>
              </Alert>
            )}

            <Flex justify="flex-end">
              <Button
                onClick={handleSubmitForm}
                disabled={!canSubmit || isCreatingJiraTicket}
                isLoading={isCreatingJiraTicket}
                loadingText="Submitting..."
                colorScheme="brand"
                variant="solid"
                size="lg"
              >
                Submit Form
              </Button>
            </Flex>
          </VStack>
        </Card>
      )}

      {/* Sticky Save Bar - Only show when there are unsaved changes and not locked (or admin editing premium) */}
      {hasUnsavedChanges && (!isLocked || canEditPremium) && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          maxW="500px"
        >
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack spacing={6}>
              <Text>You have unsaved changes</Text>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
                colorScheme="yellow"
                variant="solid"
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}
    </>
  );
};

export default SpecialtySubmit;
